'use client';

import { useState } from 'react';
import { Map } from 'lucide-react';
import type { Country } from '@/hooks/useCountries';

interface CountryCardProps {
  country: Country;
  onCompare?: (country: Country) => void;
  showRank?: boolean;
}

export default function CountryCard({ country, onCompare, showRank = true }: CountryCardProps) {
  const [imageError, setImageError] = useState(false);

  const formatArea = (area: number) => {
    if (area >= 1000000) {
      return `${(area / 1000000).toFixed(1)}M km²`;
    } else if (area >= 1000) {
      return `${(area / 1000).toFixed(1)}K km²`;
    } else {
      return `${area.toFixed(1)} km²`;
    }
  };

  const handleCompare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCompare?.(country);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 hover:shadow-md transition-shadow duration-200">
      {/* 第一行：国旗和面积信息 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex-shrink-0">
          {country.flag && !imageError ? (
            <img
              src={country.flag}
              alt={`${country.name} flag`}
              width={32}
              height={24}
              className="w-8 h-6 object-cover rounded border border-slate-200"
              onError={() => setImageError(true)}
              loading="lazy"
            />
          ) : (
            <div className="w-8 h-6 bg-slate-100 rounded border border-slate-200 flex items-center justify-center">
              <span className="text-slate-400 text-xs font-medium">
                {country.iso2 || country.iso3}
              </span>
            </div>
          )}
        </div>
        
        <div className="flex-shrink-0 text-right">
          <p className="text-sm font-semibold text-slate-900">
            {formatArea(country.area_km2)}
          </p>
          {showRank && (
            <p className="text-xs text-slate-400">
              #{country.area_rank}
            </p>
          )}
        </div>
      </div>

      {/* 第二行：国家名和大洲 */}
      <div className="mb-3">
        <h3 className="font-semibold text-slate-900 truncate text-sm mb-1">
          {country.name}
        </h3>
        <p className="text-xs text-slate-500">
          {country.continent}
        </p>
      </div>

      {/* 操作按钮 */}
      <div className="flex gap-2">
        <button
          onClick={handleCompare}
          className="w-full bg-sky-700 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-sky-800 transition-colors flex items-center justify-center gap-1"
        >
          <Map className="h-3 w-3" />
          Explore On Map
        </button>
      </div>
    </div>
  );
}
